import { addDays, addMonths, format } from 'date-fns';
import moment from 'moment';
import { nanoid } from 'nanoid';
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { BiPlusCircle } from 'react-icons/bi';
import { FaMoneyBillWave } from 'react-icons/fa';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import {
  InputDate,
  InputMoney,
  InputNumber,
  InputSelect,
} from '~/components/NovosInputs';
import useDebounce from '~/hooks/useDebounce';
import { useMediaQuery } from '~/hooks/useMediaQuery';
import api from '~/services/api';
import { moneyFormat } from '~/utils/functions';
import { useFocusTabFinalizacao } from '../../hooks/useFocusTabFinalizacao';
import { usePdvOnline } from '../../hooks/usePdvOnline';
import { tabFinalizacaoServices } from '../../services/tab-finalizacao';
import {
  BandeirasProps,
  FinalizadorasTableProps,
  SelectType,
} from '../../types';
import { BandeiraComTipoOperacao } from '../../types/context';
import { FinalizadorasPedidoProps } from '../../types/services';
import { InputFinalizadora } from '../InputFinalizadora';
import { FormaPagamentoGrid } from '../TabFinalizacao/components/FormaPagamentoGrid';
import { Totalizadores } from '../Totalizadores';
import {
  ButtonAddFormaPgto,
  FormaPagamentoContainer,
  FormaPagamentoContent,
  SeletorFinalizadora,
  TableContainer,
} from './styles';
import { buttonsFinalizadoras } from './utils/buttonsFinalizadoras';
import { formataValorNumerico } from './utils/formataValorNumerico';
import { mergeArrays } from './utils/mergeArrays';
import { resetFormaPagamento } from './utils/resetFormaPagamento';
import { tipoOperacaoCartao } from './utils/tipoOperacaoCartao';

const MySwal = withReactContent(Swal);

export const FormaPagamento: React.FC = () => {
  const {
    isOpenModalFunction,
    finalizacaoContainertRef,
    initialStatePdv,
    handleAddFinalizadoraTable,
    isModalOpen,
    handleAddFinalizadoraPedido,
    resta,
    finalizadorasTable,
    produtosTable,
    handleChangeOpenModalDesconto,
    isOpenModalDesconto,
    val_desconto,
    finalizadorasPedido,
    handleQtdParcelas,
    setBandeiraSelecionada,
  } = usePdvOnline();

  const { debouncedFn, cancelDebounce } = useDebounce();
  const changeLabelQtdParcel = useMediaQuery({ maxWidth: 1800 });

  const [iniciaRequest, setIniciaRequest] = useState(false);
  const [codFinalizadora, setCodFinalizadora] = useState<number | null>(null);
  const [numPrazo, setNumPrazo] = useState(0);
  const [codCondicao, setCodCondicao] = useState(0);
  const [desCondicao, setDesCondicao] = useState('');
  const [fetching, setFetching] = useState(false);
  const [disabledButtonLabel, setDisabledButtonLabel] = useState<string | null>(
    null,
  );
  const [tipoFinalizadora, setTipoFinalizadora] = useState<number | null>(null);
  const [bandeiras, setBandeiras] = useState<SelectType[]>([]);
  const [showBandeiras, setShowBandeiras] = useState(false);
  const [todasBandeiras, setTodasBandeiras] = useState<BandeirasProps[]>([]);
  const [tipoOperacao, setTipoOperacao] = useState(-1);

  const { handleInputFocus } = useFocusTabFinalizacao();

  const buttonAddParcelaRef = useRef<HTMLButtonElement | null>(null);
  const formaPgtoRef = useRef<HTMLDivElement | null>(null);
  const isClearingRef = useRef(false);
  const selectionVersionRef = useRef(0);
  const skipNextTeclaLookupRef = useRef(false);

  const {
    register,
    control,
    watch,
    setValue,
    reset,
    resetField,
    getValues,
    formState: { errors },
  } = useForm();

  const desTeclaWatch: string | null = watch('des_tecla');
  const vlrParcelaWatch = watch('vlr_parcela');
  const vencParcWatch = watch('venc_parcela');

  const delay = useCallback((ms: number): Promise<void> => {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }, []);

  const startSelectionChange = useCallback(() => {
    selectionVersionRef.current += 1;
    skipNextTeclaLookupRef.current = false;
    cancelDebounce();

    return selectionVersionRef.current;
  }, [cancelDebounce]);

  const isCurrentSelection = useCallback((version: number) => {
    return selectionVersionRef.current === version && !isClearingRef.current;
  }, []);

  const disableAdd =
    isOpenModalFunction ||
    isModalOpen.isOpen ||
    !desTeclaWatch ||
    !vlrParcelaWatch ||
    vlrParcelaWatch === '0,00' ||
    fetching ||
    produtosTable.length === 0;

  useEffect(() => {
    const handleSeletorFinalizadora = () => {
      if (!finalizacaoContainertRef.current || !initialStatePdv) return;

      const seletorFinalizadora =
        finalizacaoContainertRef.current.querySelector(
          '[name="cod_finalizadora"]',
        );

      if (seletorFinalizadora) {
        const { activeElement } = document;

        if (activeElement === seletorFinalizadora) setIniciaRequest(true);
      }
    };

    window.addEventListener('focusin', handleSeletorFinalizadora);
    return () =>
      window.removeEventListener('focusin', handleSeletorFinalizadora);
  }, [finalizacaoContainertRef, initialStatePdv, codFinalizadora]);

  const getTeclaByFinalizadora = useCallback(async () => {
    if (!codFinalizadora || !initialStatePdv) return;

    const requestVersion = selectionVersionRef.current;
    const codFinalizadoraAtRequest = codFinalizadora;

    const desTecla = await tabFinalizacaoServices.getTeclaFinalizadora(
      initialStatePdv.cod_loja,
      codFinalizadoraAtRequest,
    );

    if (!isCurrentSelection(requestVersion)) return;
    if (codFinalizadora !== codFinalizadoraAtRequest) return;

    const currentDate = new Date();

    if (desTecla.success) {
      const { des_tecla, cod_condicao, num_condicao, cod_finalizadora } =
        desTecla.message;

      if (!des_tecla) {
        skipNextTeclaLookupRef.current = true;
        setValue('des_tecla', '');
        setValue('vlr_parcela', '');
        handleInputFocus('des_tecla');
        return toast.warning(
          'Tecla não encontrada, verifique o cadastro de finalizadora',
        );
      }

      skipNextTeclaLookupRef.current = true;
      setValue('des_tecla', des_tecla);
      handleInputFocus('vlr_pgto');

      const numCondicao = num_condicao === 30 ? 31 : num_condicao;

      const dtaVencParcela = addDays(currentDate, numCondicao);

      setValue('venc_parcela', moment(dtaVencParcela).format('YYYY-MM-DD'));
      setNumPrazo(num_condicao);

      const condicao = await tabFinalizacaoServices.getCondicaoPagamento(
        cod_condicao,
      );

      if (!isCurrentSelection(requestVersion)) return;
      if (codFinalizadora !== codFinalizadoraAtRequest) return;

      setDesCondicao(condicao.des_condicao);
      setCodFinalizadora(cod_finalizadora);
      setCodCondicao(cod_condicao);
      if (resta > 0) setValue('vlr_parcela', moneyFormat(resta.toFixed(2)));
    }
  }, [
    codFinalizadora,
    handleInputFocus,
    initialStatePdv,
    isCurrentSelection,
    resta,
    setValue,
  ]);

  useEffect(() => {
    getTeclaByFinalizadora();
  }, [codFinalizadora, getTeclaByFinalizadora]);

  const getBandeiras = useCallback(async () => {
    try {
      const codLoja = initialStatePdv?.cod_loja ?? 1;

      const { data } = await api.get(`/pdv-online/bandeiras/${codLoja}`);

      if (!data.success) {
        setBandeiras([{ value: -1, label: 'Nenhuma' }]);
        setTodasBandeiras([]);
        return;
      }

      const opts: SelectType[] = [
        { value: -1, label: 'Nenhuma' },
        ...data.data.map((f: BandeirasProps) => ({
          value: f.cod_seq_bandeira,
          label: `${f.des_bandeira} | ${
            f.des_administradora
          } | % Créd: ${moneyFormat(
            f.per_taxa_credito.toFixed(2),
          )} | % Déb: ${moneyFormat(
            f.per_taxa_debito.toFixed(2),
          )}`.toUpperCase(),
        })),
      ];

      setBandeiras(opts);
      setTodasBandeiras(data.data);
    } catch (error) {
      setBandeiras([{ value: -1, label: 'Nenhuma' }]);
      setTodasBandeiras([]);
    }
  }, [initialStatePdv]);

  useEffect(() => {
    if (showBandeiras) getBandeiras();
  }, [getBandeiras, showBandeiras]);

  const handleResetFields = useCallback(
    (message: string) => {
      isClearingRef.current = true;
      startSelectionChange();

      setCodFinalizadora(null);
      resetField('cod_finalizadora');
      resetField('des_tecla');
      setValue('qtd_parcelas', 1);
      resetField('venc_parcela');
      setValue('vlr_parcela', '');

      if (message.length > 0) toast.warning(message);

      setTimeout(() => {
        isClearingRef.current = false;
      }, 0);
    },
    [resetField, setValue, startSelectionChange],
  );

  const handleSuccessfulFetch = async (
    data: any,
    requestVersion: number,
    teclaAtRequest: string,
  ) => {
    await delay(200);

    if (!isCurrentSelection(requestVersion)) return;
    if (getValues('des_tecla') !== teclaAtRequest) return;

    setValue('cod_finalizadora', {
      value: data.cod_finalizadora,
      label: data.des_finalizadora,
    });

    await delay(200);

    if (!isCurrentSelection(requestVersion)) return;
    if (getValues('des_tecla') !== teclaAtRequest) return;

    const inputFinalizadora = document.getElementsByName(
      'cod_finalizadora',
    )[0] as HTMLInputElement | null;

    if (inputFinalizadora) inputFinalizadora.value = data.des_finalizadora;

    const numCondicao = data.num_condicao === 30 ? 31 : data.num_condicao;

    const currentDate = new Date();
    const dtaVencParcela = addDays(currentDate, numCondicao);

    setCodFinalizadora(data.cod_finalizadora);
    setValue('venc_parcela', moment(dtaVencParcela).format('YYYY-MM-DD'));
    handleInputFocus('vlr_parcela');
    setNumPrazo(data.num_condicao);

    const condicao = await tabFinalizacaoServices.getCondicaoPagamento(
      data.cod_condicao,
    );

    if (!isCurrentSelection(requestVersion)) return;
    if (getValues('des_tecla') !== teclaAtRequest) return;

    setDesCondicao(condicao.des_condicao);
    setCodCondicao(data.cod_condicao);

    const hasBandeiras =
      Number(data?.tipo_operacao_cartao ?? 0) === 0 ||
      Number(data?.tipo_operacao_cartao ?? 0) === 1;

    setShowBandeiras(hasBandeiras);

    if (hasBandeiras) {
      setTipoOperacao(Number(data.tipo_operacao_cartao ?? 0));
      setValue(
        'tipo_operacao_cartao',
        tipoOperacaoCartao.find(
          (t) => Number(t.value) === Number(data.tipo_operacao_cartao ?? 0),
        ),
      );
    } else {
      setTipoOperacao(-1);
      setValue('tipo_operacao_cartao', tipoOperacaoCartao[0]);
    }
  };

  useEffect(() => {
    // não dispara se estamos limpando ou se não há tecla válida
    if (isClearingRef.current) return;
    if (skipNextTeclaLookupRef.current) {
      skipNextTeclaLookupRef.current = false;
      return;
    }
    if (!desTeclaWatch || desTeclaWatch.length === 0) return;
    if (desTeclaWatch.length > 3 || !initialStatePdv) return;

    const requestVersion = selectionVersionRef.current;
    const teclaAtRequest = desTeclaWatch;

    const fetchData = async () => {
      const { success, data, message } =
        await tabFinalizacaoServices.getFinalizadoraByTecla(
          initialStatePdv.cod_loja,
          teclaAtRequest,
        );

      // aborta se a tecla mudou durante a requisição ou se estamos limpando
      if (!isCurrentSelection(requestVersion)) return;
      if (isClearingRef.current) return;
      if (getValues('des_tecla') !== teclaAtRequest) return;

      if (!success || !data) {
        // só reseta se o valor atual ainda é a mesma tecla solicitada
        if (getValues('des_tecla') === teclaAtRequest) {
          handleResetFields(message);
        }
        return;
      }

      await handleSuccessfulFetch(data, requestVersion, teclaAtRequest);
    };

    debouncedFn(fetchData, 800);

    // cancela qualquer debounce pendente ao trocar a tecla/umount
    return () => cancelDebounce();
  }, [
    desTeclaWatch,
    initialStatePdv,
    debouncedFn,
    cancelDebounce,
    getValues,
    handleResetFields,
    isCurrentSelection,
  ]);

  const handleFormaPagamentoAdd = useCallback(async () => {
    if (fetching) return;

    if (Number(resta) === 0)
      return toast.warning('O valor a pagar já está completo.');

    if (!desTeclaWatch || !codFinalizadora)
      return handleInputFocus('des_tecla');

    if (!vlrParcelaWatch || vlrParcelaWatch === '0,00')
      return handleInputFocus('vlr_parcela');

    if (!vencParcWatch) return handleInputFocus('venc_parcela');

    const qtdParcelas = Number(getValues('qtd_parcelas')) || 1;
    handleQtdParcelas(qtdParcelas);
    const parcelasExistentes: FinalizadorasTableProps[] = [
      ...(finalizadorasTable || []),
    ];
    const novasParcelas: FinalizadorasTableProps[] = [];
    const novasParcelasPedido: FinalizadorasPedidoProps[] = [];

    const existeFinalizadoraTipoDinheiro = parcelasExistentes.some(
      (parcela) => parcela.tipo_finalizadora === 0,
    );

    if (existeFinalizadoraTipoDinheiro && tipoFinalizadora === 0) {
      const { isConfirmed } = await MySwal.fire({
        title: 'Deseja alterar parcela dinheiro?',
        text: 'Já existe uma parcela como dinheiro lançada.',
        showCancelButton: true,
        confirmButtonColor: '#8850BF',
        cancelButtonColor: '#DE350B',
        confirmButtonText: 'Sim',
        cancelButtonText: 'Não',
        icon: 'warning',
      });
      if (!isConfirmed) return;
    }

    const dataVencimento = (numMonths: number) =>
      format(
        addMonths(addDays(new Date(getValues('venc_parcela')), 1), numMonths),
        'dd/MM/yyyy',
      );

    if (qtdParcelas > 1) {
      const { isConfirmed } = await MySwal.fire({
        title: 'Dividir ou replicar?',
        showCancelButton: true,
        confirmButtonColor: '#8850BF',
        cancelButtonColor: '#2B78E4',
        confirmButtonText: 'Dividir',
        cancelButtonText: 'Replicar',
        icon: 'question',
        didOpen: () => {
          const modals = document.querySelectorAll('.modal');
          modals.forEach((m) => m.removeAttribute('tabindex'));

          Swal.getConfirmButton()?.focus();
        },
        didClose: () => {
          const modals = document.querySelectorAll('.modal');
          modals.forEach((m) => m.setAttribute('tabindex', '-1'));
        },
      });

      const gerarParcelas = (valorParcelaCallback: any) => {
        for (let i = 0; i < qtdParcelas; i++) {
          const parcelaId = nanoid();
          const valParcelaAtual = valorParcelaCallback(i);

          const parcela: FinalizadorasTableProps = {
            id: parcelaId,
            num_prazo: numPrazo,
            condicao: desCondicao,
            des_finalizadora: getValues('cod_finalizadora').label,
            dta_vencto: dataVencimento(i),
            vlr_parcela: moneyFormat(valParcelaAtual.toFixed(2)),
            tipo_finalizadora: tipoFinalizadora,
            cod_finalizadora: codFinalizadora,
          };

          const parcelaPedido: FinalizadorasPedidoProps = {
            id: parcelaId,
            cod_finalizadora: codFinalizadora,
            cod_condicao: codCondicao,
            num_condicao: numPrazo,
            val_parcela: formataValorNumerico(valParcelaAtual.toFixed(2)),
            dta_vencimento: dataVencimento(i),
          };

          novasParcelas.push(parcela);
          novasParcelasPedido.push(parcelaPedido);
          parcelasExistentes.push(parcela);
        }
      };

      if (isConfirmed) {
        const valorTotal = formataValorNumerico(getValues('vlr_parcela'));
        const valorParcelaAjustada = parseFloat(
          (valorTotal / qtdParcelas).toFixed(2),
        );

        gerarParcelas((i: number) => {
          return i === qtdParcelas - 1
            ? valorTotal - valorParcelaAjustada * (qtdParcelas - 1)
            : valorParcelaAjustada;
        });
      } else {
        gerarParcelas(() => formataValorNumerico(getValues('vlr_parcela')));
      }
    } else {
      const finalizadoraDinheiro = finalizadorasTable.find(
        (f) =>
          f.tipo_finalizadora === tipoFinalizadora &&
          f.des_finalizadora === 'DINHEIRO',
      );

      if (finalizadoraDinheiro) {
        finalizadoraDinheiro.vlr_parcela = getValues('vlr_parcela');

        const parcelPedido: FinalizadorasPedidoProps = {
          id: String(finalizadoraDinheiro.id),
          cod_finalizadora: codFinalizadora,
          cod_condicao: codCondicao,
          num_condicao: numPrazo,
          val_parcela: formataValorNumerico(finalizadoraDinheiro.vlr_parcela),
          dta_vencimento: finalizadoraDinheiro.dta_vencto,
        };

        handleAddFinalizadoraTable(finalizadoraDinheiro);
        handleAddFinalizadoraPedido(parcelPedido);

        isClearingRef.current = true;
        startSelectionChange();

        reset(resetFormaPagamento);
        setCodFinalizadora(null);
        setNumPrazo(0);
        setDesCondicao('');
        setDisabledButtonLabel(null);

        setTimeout(() => {
          isClearingRef.current = false;
        }, 0);
        return;
      }

      // Valida valor de DINHEIRO com DESCONTO para não permitir valores abaixo do DESCONTO (Regra de DESCONTO/ACRÉSCIMO)
      if (
        tipoFinalizadora === 0 &&
        formataValorNumerico(getValues('vlr_parcela')) < val_desconto
      ) {
        return toast.warning(
          `Valor de finalizadora Dinheiro inferior ao valor atual de Desconto. Informe um valor de finalizadora Dinheiro maior que R$ ${moneyFormat(
            val_desconto.toFixed(2),
          )}`,
        );
      }

      const parcelaId = nanoid();

      const parcela: FinalizadorasTableProps = {
        id: parcelaId,
        num_prazo: numPrazo,
        condicao: desCondicao,
        des_finalizadora: getValues('cod_finalizadora').label,
        dta_vencto: dataVencimento(0),
        vlr_parcela: getValues('vlr_parcela'),
        tipo_finalizadora: tipoFinalizadora,
        cod_finalizadora: codFinalizadora,
      };

      const parcelaPedido: FinalizadorasPedidoProps = {
        id: parcelaId,
        cod_finalizadora: codFinalizadora,
        cod_condicao: codCondicao,
        num_condicao: numPrazo,
        val_parcela: formataValorNumerico(getValues('vlr_parcela')),
        dta_vencimento: dataVencimento(0),
      };

      novasParcelas.push(parcela);
      novasParcelasPedido.push(parcelaPedido);
      parcelasExistentes.push(parcela);
    }

    handleAddFinalizadoraTable(
      qtdParcelas > 1 ? parcelasExistentes : novasParcelas[0],
    );
    handleAddFinalizadoraPedido(
      qtdParcelas > 1
        ? mergeArrays(novasParcelasPedido, finalizadorasPedido)
        : novasParcelasPedido[0],
    );

    isClearingRef.current = true;
    startSelectionChange();

    reset(resetFormaPagamento);
    await delay(200);
    handleResetFields('');

    await delay(100);

    const inputFinalizadora = document.getElementsByName(
      'cod_finalizadora',
    )[0] as HTMLInputElement | null;
    if (inputFinalizadora) inputFinalizadora.value = '';

    setCodFinalizadora(null);
    setNumPrazo(0);
    setDesCondicao('');
    setDisabledButtonLabel(null);
    setShowBandeiras(false);

    // libera a flag
    setTimeout(() => {
      isClearingRef.current = false;
    }, 0);
  }, [
    codCondicao,
    codFinalizadora,
    desCondicao,
    desTeclaWatch,
    fetching,
    finalizadorasTable,
    getValues,
    handleAddFinalizadoraPedido,
    handleAddFinalizadoraTable,
    handleInputFocus,
    numPrazo,
    reset,
    resta,
    tipoFinalizadora,
    vencParcWatch,
    vlrParcelaWatch,
    finalizadorasPedido,
    handleQtdParcelas,
    delay,
    handleResetFields,
    startSelectionChange,
  ]);

  useEffect(() => {
    const handleKeyEnterPress = (ev: KeyboardEvent) => {
      if (
        finalizacaoContainertRef &&
        ev.key === 'Enter' &&
        buttonAddParcelaRef.current &&
        !fetching
      ) {
        buttonAddParcelaRef.current.click();
      }
    };

    window.addEventListener('keydown', handleKeyEnterPress);
    return () => window.removeEventListener('keydown', handleKeyEnterPress);
  }, [fetching, finalizacaoContainertRef]);

  const handlePaymentMethodSelection = useCallback(
    async (paymentMethodLabel: string) => {
      if (!initialStatePdv) return;

      const requestVersion = startSelectionChange();

      const paymentMethodMappings: Record<
        string,
        { tipoFinalizadora: number; tipoOperacaoCartao: number }
      > = {
        dinheiro: { tipoFinalizadora: 0, tipoOperacaoCartao: -1 },
        crédito: { tipoFinalizadora: 1, tipoOperacaoCartao: 0 },
        débito: { tipoFinalizadora: 1, tipoOperacaoCartao: 1 },
        pix: { tipoFinalizadora: 1, tipoOperacaoCartao: 2 },
        'a receber': { tipoFinalizadora: 4, tipoOperacaoCartao: -1 },
      };

      const normalizedLabel = paymentMethodLabel.toLowerCase();
      const selectedPaymentMethod = paymentMethodMappings[normalizedLabel];

      if (!selectedPaymentMethod) {
        setDisabledButtonLabel(null);
        return toast.warning('Este método de pagamento não está configurado');
      }

      setDisabledButtonLabel(paymentMethodLabel);
      setValue('qtd_parcelas', '1');

      if (selectedPaymentMethod) {
        setFetching(true);

        const { data } = await api.get(
          `/finalizadoras/${initialStatePdv.cod_loja}`,
          {
            params: {
              'finalizadora.tipo_finalizadora':
                selectedPaymentMethod.tipoFinalizadora,
              'finalizadora.tipo_operacao_cartao':
                selectedPaymentMethod.tipoOperacaoCartao,
            },
          },
        );

        if (!isCurrentSelection(requestVersion)) return;

        if (!data.success || data.data.length === 0) {
          setDisabledButtonLabel(null);
          setFetching(false);
          return toast.warning(
            'Não há uma finalizadora cadastrada com essas características',
          );
        }

        if (data.success) {
          let selectedFinalizadora;

          setTipoFinalizadora(selectedPaymentMethod.tipoFinalizadora);

          if (data.data.length > 1) {
            selectedFinalizadora =
              data.data.find((item: any) =>
                item.des_finalizadora.toLowerCase().includes(normalizedLabel),
              ) || data.data[1];
          } else [selectedFinalizadora] = data.data;

          const { cod_finalizadora, des_finalizadora } = selectedFinalizadora;

          setCodFinalizadora(Number(cod_finalizadora));
          setValue('cod_finalizadora', {
            value: cod_finalizadora,
            label: des_finalizadora,
          });

          const hasBandeiras =
            Number(selectedPaymentMethod.tipoOperacaoCartao) === 0 ||
            Number(selectedPaymentMethod.tipoOperacaoCartao) === 1;

          setShowBandeiras(hasBandeiras);

          if (hasBandeiras) {
            setTipoOperacao(Number(selectedPaymentMethod.tipoOperacaoCartao));
            setValue(
              'tipo_operacao_cartao',
              tipoOperacaoCartao.find(
                (t) =>
                  Number(t.value) ===
                  Number(selectedPaymentMethod.tipoOperacaoCartao),
              ),
            );
          } else {
            setTipoOperacao(-1);
            setValue('tipo_operacao_cartao', tipoOperacaoCartao[0]);
          }

          if (resta > 0) setValue('vlr_parcela', moneyFormat(resta.toFixed(2)));
        }
      }
      await delay(1000);
      if (isCurrentSelection(requestVersion)) setFetching(false);
    },
    [
      delay,
      initialStatePdv,
      isCurrentSelection,
      resta,
      setValue,
      startSelectionChange,
    ],
  );

  const handleMouseDown = (event: any) => {
    if (event.detail > 1) event.preventDefault();
  };

  const handleDragStart = (event: any) => {
    event.preventDefault();
  };

  return (
    <FormaPagamentoContainer>
      <Row>
        <Col sm={12} md={7}>
          <div className="buttons-finalizadoras">
            {buttonsFinalizadoras.map(({ label, icon: Icon }, idx) => (
              <button
                key={label}
                className="btn-finalizadora"
                type="button"
                name={`forma_finalizadora_${idx}`}
                disabled={fetching || disabledButtonLabel === label}
                onClick={async () => {
                  await handlePaymentMethodSelection(label);
                }}
              >
                <Icon color="white" size={20} />
                {label.toUpperCase()}
              </button>
            ))}
          </div>
        </Col>
        <Col sm={12} md={5}>
          <div className="buttons-desconto">
            <button
              className="btn-desconto"
              type="button"
              disabled={produtosTable.length === 0}
              onClick={() => {
                setTimeout(() => {
                  document.getElementById('perc_desconto')?.focus();
                }, 50);
                handleChangeOpenModalDesconto(true);
              }}
            >
              <FaMoneyBillWave size={20} />
              DESCONTO/ACRÉSCIMO
            </button>
          </div>
        </Col>
      </Row>

      <FormaPagamentoContent ref={formaPgtoRef}>
        <InputNumber
          label="Tecla:"
          min={1}
          maxLength={3}
          step="1"
          max={999}
          name="des_tecla"
          placeholder="0"
          register={register}
          control={control}
          isError={!!errors.des_tecla}
          autoComplete="off"
          disabled={isOpenModalFunction || isModalOpen.isOpen || fetching}
          onInput={(ev: ChangeEvent<HTMLInputElement>) => {
            const { value } = ev.target;
            startSelectionChange();
            setValue('des_tecla', value);
            setDisabledButtonLabel(null);
          }}
          onMouseDown={handleMouseDown}
          onDragStart={handleDragStart}
        />
        <SeletorFinalizadora>
          <InputFinalizadora
            name="cod_finalizadora"
            register={register}
            isError={!!errors.cod_finalizadora}
            control={control}
            changeSelected={(selected: any) => {
              const { value, label, tipo_finalizadora, tipo_operacao_cartao } =
                selected;
              startSelectionChange();
              setCodFinalizadora(Number(value));
              setValue('cod_finalizadora', { value, label });
              setDisabledButtonLabel(null);
              setTipoFinalizadora(Number(tipo_finalizadora));

              const hasBandeiras =
                Number(tipo_operacao_cartao) === 0 ||
                Number(tipo_operacao_cartao) === 1;

              setShowBandeiras(hasBandeiras);

              if (hasBandeiras) {
                setTipoOperacao(Number(tipo_operacao_cartao));
                setValue(
                  'tipo_operacao_cartao',
                  tipoOperacaoCartao.find(
                    (t) => Number(t.value) === Number(tipo_operacao_cartao),
                  ),
                );
              } else {
                setTipoOperacao(-1);
                setValue('tipo_operacao_cartao', tipoOperacaoCartao[0]);
              }
            }}
            disabled={isOpenModalFunction || isModalOpen.isOpen || fetching}
            codLoja={initialStatePdv ? initialStatePdv.cod_loja : 1}
            iniciaRequest={iniciaRequest}
          />
        </SeletorFinalizadora>
        <InputMoney
          label="Valor:"
          placeholder="0,00"
          min={0}
          decimals={2}
          name="vlr_parcela"
          register={register}
          autoComplete="off"
          control={control}
          showIcon={false}
          isError={!!errors.vlr_parcela}
          onInput={(ev: any) => {
            setValue('vlr_parcela', ev.target.value);
          }}
          disabled={
            isOpenModalFunction ||
            isModalOpen.isOpen ||
            fetching ||
            !codFinalizadora ||
            isOpenModalDesconto
          }
          style={{ width: '8.125rem' }}
          onMouseDown={handleMouseDown}
          onDragStart={handleDragStart}
        />
        <InputNumber
          label={changeLabelQtdParcel ? 'Parcelas:' : 'Qtd Parcelas:'}
          min={1}
          maxLength={2}
          step="1"
          placeholder="1"
          max={99}
          name="qtd_parcelas"
          register={register}
          control={control}
          isError={!!errors.qtd_parcelas}
          autoComplete="off"
          disabled={
            isOpenModalFunction ||
            isModalOpen.isOpen ||
            fetching ||
            tipoFinalizadora === 0
          }
          onInput={(ev: any) => {
            setValue('qtd_parcelas', ev.target.value);
          }}
          style={{ width: '8.75rem' }}
          onMouseDown={handleMouseDown}
          onDragStart={handleDragStart}
        />
        <InputDate
          label="Vencimento 1ª Parc."
          name="venc_parcela"
          register={register}
          control={control}
          disabled={
            isOpenModalFunction ||
            isModalOpen.isOpen ||
            fetching ||
            tipoFinalizadora === 0
          }
          isError={!!errors.venc_parcela}
          onChange={(ev: any) => {
            setValue('venc_parcela', ev.target.value);
          }}
          onMouseDown={handleMouseDown}
          onDragStart={handleDragStart}
        />
        <ButtonAddFormaPgto
          ref={buttonAddParcelaRef}
          onClick={async () => {
            setTimeout(async () => {
              await handleFormaPagamentoAdd();
            }, 100);
          }}
          disabled={disableAdd}
        >
          <BiPlusCircle size={20} />
          Adicionar
        </ButtonAddFormaPgto>
      </FormaPagamentoContent>
      {showBandeiras && (
        <div className="container-operacao-bandeira">
          <InputSelect
            label="Operação"
            placeholder="Nenhum"
            maxLength={7}
            name="tipo_operacao_cartao"
            register={register}
            isError={false}
            control={control}
            options={tipoOperacaoCartao}
            changeSelected={(selected) => {
              setValue('tipo_operacao_cartao', {
                value: selected.value,
                label: selected.label,
              });
              setTipoOperacao(Number(selected.label));
            }}
          />
          <div className="campo-bandeira">
            <InputSelect
              label="Bandeira"
              placeholder="Nenhuma"
              name="cod_seq_bandeira"
              register={register}
              isError={false}
              control={control}
              options={bandeiras}
              changeSelected={(selected) => {
                setValue('cod_seq_bandeira', {
                  value: selected.value,
                  label: selected.label,
                });

                const bandeiraSelecionada = todasBandeiras.find(
                  (t) => Number(t.cod_seq_bandeira) === Number(selected.value),
                );

                if (!bandeiraSelecionada) {
                  setBandeiraSelecionada([]);
                  return;
                }

                const flag: BandeiraComTipoOperacao = {
                  ...bandeiraSelecionada,
                  tipo_operacao_cartao: tipoOperacao,
                  cod_finalizadora: codFinalizadora ?? 0,
                };

                setBandeiraSelecionada((prev) => {
                  const semDuplicata = prev.filter(
                    (item) => item.cod_finalizadora !== flag.cod_finalizadora,
                  );

                  return [...semDuplicata, flag];
                });
              }}
            />
          </div>
        </div>
      )}
      <TableContainer>
        <FormaPagamentoGrid />
      </TableContainer>
      <div style={{ marginTop: '0.625rem' }} />
      <Totalizadores />
    </FormaPagamentoContainer>
  );
};
